import { useCallback, useState } from "react";
import { apiErrorMessage } from "@/lib/api";
import { uploadMedia } from "./serviceApi";

let seq = 0;

// Queue of files uploaded sequentially to a service record with progress + retry.
export function useUploadQueue() {
  const [items, setItems] = useState([]);
  const patch = (key, p) => setItems((list) => list.map((i) => (i.key === key ? { ...i, ...p } : i)));

  const add = useCallback((files, opts = {}) => {
    setItems((list) => [...list, ...files.map((file) => ({ key: `u${(seq += 1)}`, file, status: "queued", progress: 0, autoCaption: !!opts.autoCaption }))]);
  }, []);
  const remove = (it) => setItems((list) => list.filter((i) => i.key !== it.key));
  const reset = () => setItems([]);

  const runOne = async (client, recordId, it) => {
    patch(it.key, { status: "uploading", error: null });
    try {
      await uploadMedia(client, { recordId }, it.file, (p, st) => patch(it.key, { progress: p, status: st === "done" ? "uploading" : st || "uploading" }),
        { autoCaption: it.autoCaption, uploadId: it.uploadId, onSession: (id) => patch(it.key, { uploadId: id }) });
      patch(it.key, { status: "done", progress: 100 });
      return true;
    } catch (e) {
      patch(it.key, { status: "error", error: apiErrorMessage(e, "Upload failed") });
      return false;
    }
  };

  // Two files at a time; each file already sends 3 chunks in parallel.
  const runAll = async (client, recordId, list) => {
    const todo = list.filter((it) => it.status !== "done");
    let failed = 0;
    let next = 0;
    const worker = async () => {
      while (next < todo.length) {
        const it = todo[next]; next += 1;
        // eslint-disable-next-line no-await-in-loop
        if (!(await runOne(client, recordId, it))) failed += 1;
      }
    };
    await Promise.all([worker(), worker()]);
    return failed;
  };

  const addAndRun = async (client, recordId, files, opts = {}) => {
    const its = files.map((file) => ({ key: `u${(seq += 1)}`, file, status: "queued", progress: 0, autoCaption: !!opts.autoCaption }));
    setItems((list) => [...list.filter((i) => i.status !== "done"), ...its]);
    return runAll(client, recordId, its);
  };

  return { items, add, remove, reset, runAll, runOne, addAndRun };
}
